import { useCallback, useEffect, useRef, useState } from 'react'

// Ресайз колонок таблицы квот на странице «Выбор квот».
// Ширины сохраняются в localStorage и применяются ко всем раскрытым позициям.
// Ключи колонок фиксированы (см. COLUMN_KEYS) — добавлять/убирать только вместе
// с PositionCard.tsx.

export type ColumnKey =
  | 'radio'
  | 'min'
  | 'matrix'
  | 'supplier'
  | 'procurer'
  | 'date'
  | 'price'
  | 'note'

export const COLUMN_KEYS: ColumnKey[] = [
  'radio',
  'min',
  'matrix',
  'supplier',
  'procurer',
  'date',
  'price',
  'note',
]

const DEFAULT_WIDTHS: Record<ColumnKey, number> = {
  radio: 44,
  min: 52,
  matrix: 140,
  supplier: 200,
  procurer: 160,
  date: 92,
  price: 180,
  note: 260,
}

const MIN_WIDTH = 40
const STORAGE_KEY = 'crm_quotes_selection_col_widths_v2'

function readStoredWidths(): Record<ColumnKey, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_WIDTHS }
    const parsed = JSON.parse(raw) as Partial<Record<ColumnKey, number>>
    const merged = { ...DEFAULT_WIDTHS }
    for (const k of COLUMN_KEYS) {
      const val = parsed[k]
      if (typeof val === 'number' && val >= MIN_WIDTH) merged[k] = val
    }
    return merged
  } catch {
    return { ...DEFAULT_WIDTHS }
  }
}

export function useColumnWidths() {
  const [widths, setWidths] = useState<Record<ColumnKey, number>>(() => readStoredWidths())

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(widths))
    } catch {
      /* ignore */
    }
  }, [widths])

  const dragRef = useRef<{ key: ColumnKey; startX: number; startW: number } | null>(null)

  const onStartResize = useCallback(
    (key: ColumnKey) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      dragRef.current = { key, startX: e.clientX, startW: widths[key] }

      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return
        const delta = ev.clientX - dragRef.current.startX
        const next = Math.max(MIN_WIDTH, dragRef.current.startW + delta)
        setWidths((prev) => ({ ...prev, [dragRef.current!.key]: next }))
      }
      const onUp = () => {
        dragRef.current = null
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [widths],
  )

  const reset = useCallback(() => setWidths({ ...DEFAULT_WIDTHS }), [])

  return { widths, onStartResize, reset }
}
